import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { typographyCss } from 'common/typography';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { useDiscountedCategory } from 'modules/campaigns/hooks/useDiscountedCategory';
import { useDiscountedItem } from 'modules/campaigns/hooks/useDiscountedItem';
import { ItemDiscountConfigFormValues, ItemDiscountType } from 'modules/campaigns/types';

export type DiscountEligibilityTextProps = {
  itemIds: string[];
  categoryIds: string[];
  menuId: string;
  itemTriggerCount: number;
  discountConfig: ItemDiscountConfigFormValues;
  isEndOfDayCampaign?: boolean;
};

const Text = styled.div`
  ${typographyCss.Body3};
`;

const ItemDiscountVariantText = ({
  itemIds,
  categoryIds,
  menuId,
  itemTriggerCount,
  discountConfig,
  isEndOfDayCampaign,
}: DiscountEligibilityTextProps) => {
  const { currency } = useCampaignModuleContext();

  // Use the item in preview only if there is exactly one item
  const singleItemOnly = itemIds.length <= 1 && !categoryIds.length;
  const itemId = singleItemOnly ? itemIds[0] : undefined;
  const { discountedItem } = useDiscountedItem({ itemId, menuId });

  const singleCategoryOnly = itemIds.length === 0 && categoryIds.length === 1;
  const categoryId = singleCategoryOnly ? categoryIds[0] : undefined;
  const { discountedCategory } = useDiscountedCategory({ categoryId, menuId });

  const { discountType, amount, freeItemsCount, percentage } = discountConfig;

  const translatedDiscountVariant = useMemo(() => {
    if (discountType === ItemDiscountType.X_FOR_FREE) {
      return (
        <FormattedMessage
          id="campaigns.sidebar-preview.banner-preview.variants.free-quantity"
          values={{
            freeItemQuantity: freeItemsCount,
          }}
        />
      );
    }

    if (discountType === ItemDiscountType.PERCENTAGE_OFF) {
      return (
        <FormattedMessage
          id={
            isEndOfDayCampaign
              ? 'campaigns.sidebar-preview.end-of-day-banner-preview.variants.percentage'
              : 'campaigns.sidebar-preview.banner-preview.variants.percentage'
          }
          values={{
            discountPercentage: percentage,
          }}
        />
      );
    }

    if (discountType === ItemDiscountType.AMOUNT_OFF && amount !== undefined) {
      return (
        <FormattedMessage
          id={
            isEndOfDayCampaign
              ? 'campaigns.sidebar-preview.end-of-day-banner-preview.variants.amount'
              : 'campaigns.sidebar-preview.banner-preview.variants.amount'
          }
          values={{
            discountAmount: <MonetaryAmount amount={amount} currency={currency} />,
          }}
        />
      );
    }

    return '';
  }, [discountType, amount, freeItemsCount, percentage, isEndOfDayCampaign, currency]);

  const translatedFullText = useMemo(() => {
    if (singleItemOnly) {
      return (
        <FormattedMessage
          id={
            isEndOfDayCampaign
              ? 'campaigns.sidebar-preview.end-of-day-banner-preview.title'
              : 'campaigns.sidebar-preview.banner-preview.title'
          }
          values={{
            itemName: discountedItem?.name ?? '',
            itemQuantity: itemTriggerCount,
            discountVariant: <b>{translatedDiscountVariant}</b>,
          }}
        />
      );
    }

    // No special banner variants for end of day campaigns, only "Selected items now X% off"
    if (isEndOfDayCampaign) {
      return (
        <FormattedMessage
          id="campaigns.sidebar-preview.end-of-day-banner-preview.title-selected-items"
          values={{
            itemQuantity: itemTriggerCount,
            discountVariant: <b>{translatedDiscountVariant}</b>,
          }}
        />
      );
    }

    if (singleCategoryOnly) {
      return (
        <FormattedMessage
          id={'campaigns.sidebar-preview.banner-preview.title-category'}
          values={{
            categoryName: discountedCategory?.name,
            itemQuantity: itemTriggerCount,
            discountVariant: <b>{translatedDiscountVariant}</b>,
          }}
        />
      );
    }

    if (!itemIds.length) {
      return (
        <FormattedMessage
          id={'campaigns.sidebar-preview.banner-preview.title-selected-categories'}
          values={{
            itemQuantity: itemTriggerCount,
            discountVariant: <b>{translatedDiscountVariant}</b>,
          }}
        />
      );
    }

    return (
      <FormattedMessage
        id={'campaigns.sidebar-preview.banner-preview.title-selected-items'}
        values={{
          itemQuantity: itemTriggerCount,
          discountVariant: <b>{translatedDiscountVariant}</b>,
        }}
      />
    );
  }, [
    singleItemOnly,
    discountedItem?.name,
    itemTriggerCount,
    translatedDiscountVariant,
    isEndOfDayCampaign,
    itemIds.length,
    singleCategoryOnly,
    discountedCategory?.name,
  ]);

  return <Text>{translatedFullText}</Text>;
};

export default ItemDiscountVariantText;
